/**
 * A message sent from a work-page handoff ("Ask in chat" / "Reject" on a task
 * or problem) starts with a one-line marker naming the item, e.g.
 * `[Rejected: Inspect the star-wheel guide]`. The marker is part of the stored
 * prompt, so the chip survives reloads and the agent reads which item the
 * message is about; the chat UI renders it as a chip above the bubble.
 */
export type HandoffChipTone = 'reject' | 'ask'

export interface HandoffChip {
  tone: HandoffChipTone
  label: string
}

const PREFIX: Record<HandoffChipTone, string> = { reject: 'Rejected', ask: 'About' }
const MARKER = /^\[(Rejected|About): ([^\]\n]+)\]\n/

function sanitizeLabel(label: string): string {
  return label.replace(/[\]\n\r]+/g, ' ').trim()
}

/** Text shown on the chip: "Rejected {title}" for a reject, the title otherwise. */
export function handoffChipText(chip: HandoffChip): string {
  return chip.tone === 'reject' ? `Rejected ${chip.label}` : chip.label
}

export function withHandoffChip(text: string, chip: HandoffChip): string {
  return `[${PREFIX[chip.tone]}: ${sanitizeLabel(chip.label)}]\n${text}`
}

export function splitHandoffChip(prompt: string): { chip: HandoffChip | null; text: string } {
  const match = MARKER.exec(prompt)
  if (!match) return { chip: null, text: prompt }
  return {
    chip: { tone: match[1] === 'Rejected' ? 'reject' : 'ask', label: match[2] },
    text: prompt.slice(match[0].length)
  }
}
